import { Button, FormikField, H1, RadioGroup, Text } from '@betterment/samba-react';
import { type StepValues } from '@betterment/shared-components';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';

import { WorkflowLayout } from '../../Shared/layout/WorkflowLayout';
import {
  TAX_COORDINATED_PORTFOLIO_FLOW_CONFIG,
  useTaxCoordinatedPortfolioFlowContext,
  useTaxCoordinatedPortfolioFlowStep,
} from '../FlowConfig';

type FormValues = StepValues<typeof TAX_COORDINATED_PORTFOLIO_FLOW_CONFIG, 'portfolio_selection'>;

export const PortfolioSelection = () => {
  const navigate = useNavigate();
  const { taxCoordinationAccountOptions } = useTaxCoordinatedPortfolioFlowContext();
  const { commit, dependencies } = useTaxCoordinatedPortfolioFlowStep('portfolio_selection');

  const selectedSubAccountIds = Object.values(dependencies.accounts_selection).filter(Boolean);
  const portfolioOptions = Array.from(
    new Map(
      taxCoordinationAccountOptions
        .filter(({ envelopeAccount }) =>
          (envelopeAccount.subAccounts ?? []).some(sa => selectedSubAccountIds.includes(sa.id))
        )
        .flatMap(({ envelopeAccount }) => {
          const { portfolio } = envelopeAccount;
          if (!portfolio) return [];
          const { name, descriptionHtml } = portfolio.strategy;
          return [[portfolio.configurationDescription, { name, description: descriptionHtml }] as const];
        })
    ).values()
  );

  const handleSubmit = (values: FormValues) => {
    commit(values);
  };

  return (
    <WorkflowLayout
      workflowTitle={null}
      onBack={() => navigate(-1)}
      onClose={() => navigate('/')}
      pageTitle="Tax Coordinated Portfolio"
    >
      <H1 variant="pageTitle" css={{ marginBottom: 'space.200' }}>
        Pick one portfolio for your accounts.
      </H1>
      <Text as="p" variant="text100" css={{ marginBottom: 'space.400' }}>
        All of your coordinated accounts will be invested in the same portfolio. Choose the one you’d like for your
        Tax-Coordinated Portfolio.
      </Text>
      <Formik<FormValues>
        initialValues={{ portfolioOption: undefined }}
        validationSchema={TAX_COORDINATED_PORTFOLIO_FLOW_CONFIG.portfolio_selection.schema}
        onSubmit={handleSubmit}
      >
        <Form>
          <FormikField name="portfolioOption">
            <FormikField.Control>
              <RadioGroup
                css={{
                  marginBottom: 'space.400',
                  rowGap: 'space.300',
                }}
              >
                {portfolioOptions.map(({ name, description }) => (
                  <RadioGroup.ItemContainer
                    key={name}
                    css={{
                      '&:not(:first-child)': {
                        borderTop: '1px solid token(colors.border.weak)',
                        paddingTop: 'space.300',
                      },
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      columnGap: 'space.100',
                    }}
                  >
                    <RadioGroup.Item value={name} />
                    <RadioGroup.ItemLabel>
                      <Text as="p" css={{ textStyle: 'title100' }}>
                        {name}
                      </Text>
                      <Text as="p" css={{ color: 'text.weak' }} dangerouslySetInnerHTML={{ __html: description! }} />
                    </RadioGroup.ItemLabel>
                  </RadioGroup.ItemContainer>
                ))}
              </RadioGroup>
            </FormikField.Control>
          </FormikField>
          <Button
            type="submit"
            css={{
              width: {
                base: '100%',
                md: 'fit-content',
              },
            }}
          >
            Continue
          </Button>
        </Form>
      </Formik>
    </WorkflowLayout>
  );
};
